import ReactECharts from 'echarts-for-react';

/**
 * BotCareerPathChart_3 — "dark command-deck" variant of BotCareerPathChart (blog03).
 *
 * Same data, same chart family (two-series line over a categorical lifecycle).
 * Direction: opaque slate-900 background, light text, teal/cyan accents, subtle
 * gridlines. Confident dashboard feel.
 */

const DECK = {
  bg: '#0F172A', // slate-900
  text: '#F8FAFC', // slate-50 — primary text
  textSecondary: '#94A3B8', // slate-400 — secondary text
  teal: '#0AB3BF', // autonomy series
  cyan: '#4DD4E0', // review series
  grid: '#1E293B', // slate-800 — subtle gridlines
  tooltipBg: '#1E293B',
  tooltipBorder: '#334155',
};

const STAGES = ['Hired', 'Probation', 'Reviewed', 'Semi-Auto', 'Full-Auto'];
// Autonomy (what the bot does on its own) and Review (what a human still gates).
const AUTONOMY = [0, 10, 30, 60, 90];
const REVIEW = [100, 90, 70, 40, 10];

/** ONE shared percentage formatter — tooltip, axis ticks, and point labels all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const BotCareerPathChart_3 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: DECK.bg,
    tooltip: {
      trigger: 'axis',
      backgroundColor: DECK.tooltipBg,
      borderColor: DECK.tooltipBorder,
      textStyle: { color: DECK.text, fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${STAGES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: DECK.textSecondary, fontSize: 13 },
      inactiveColor: DECK.textSecondary,
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 48, right: 40, top: 48, bottom: 40 },
    xAxis: {
      type: 'category',
      data: STAGES,
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: DECK.textSecondary, fontSize: 13, fontWeight: 600 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: DECK.textSecondary, fontSize: 11 },
      axisLabel: { color: DECK.textSecondary, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: DECK.grid, opacity: 0.8 } },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'line',
        smooth: true,
        symbolSize: 9,
        data: AUTONOMY,
        itemStyle: { color: DECK.teal },
        lineStyle: { color: DECK.teal, width: 3 },
        areaStyle: { color: DECK.teal, opacity: 0.15 },
        label: {
          show: true,
          position: 'top',
          color: DECK.cyan,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'line',
        smooth: true,
        symbolSize: 9,
        data: REVIEW,
        itemStyle: { color: DECK.cyan },
        lineStyle: { color: DECK.cyan, width: 3, type: 'dashed' },
        areaStyle: { color: DECK.cyan, opacity: 0.07 },
        label: {
          show: true,
          position: 'bottom',
          color: DECK.cyan,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 1. Autonomy is earned: bot autonomy rises from 0% at hire to 90% at full-auto while human review falls
        from 100% to 10% — review scales with trust but never disappears. Hover for values.
      </p>
    </div>
  );
};
